import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Compass, Loader2, Flame, Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import GalleryReportCard from "@/components/GalleryReportCard";
import DecodeMiniCard from "@/components/DecodeMiniCard";
import ShareCardComposer from "@/components/ShareCardComposer";

const TYPES = [
  "", "Personal Identity", "Artist Name", "Brand Name", "Song Title", "Pop Culture",
  "Luxury Object", "Wealth Word", "Status Word", "Shadow Word", "Relationship Word",
];

export default function Gallery() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [sort, setSort] = useState("trending");
  const [type, setType] = useState("");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const r = await api.get(`/gallery?sort=${sort}${type ? `&decode_type=${encodeURIComponent(type)}` : ""}`);
      setItems(r.data.items || []);
    } catch (e) { setItems([]); }
  }, [sort, type]);
  useEffect(() => { load(); }, [load]);

  const view = async (item) => {
    setOpen(true); setLoadingDetail(true); setDetail({ item, full: null });
    try {
      const r = await api.get(`/decode/report/${item.id}`);
      setDetail({ item, full: r.data });
    } catch (e) { /* ignore */ } finally { setLoadingDetail(false); }
  };

  const miniData = detail?.full
    ? { input_text: detail.full.input_text, mini: detail.full.mini_decode, rule_output: detail.full.rule_output_json }
    : null;

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold flex items-center gap-2"><Compass className="w-6 h-6 green-text" />Public Decode Gallery</h1>
          <p className="text-[var(--bone-muted)] text-sm">Wordbodies shared by the community. Make your own decode public from the Decode page.</p>
        </div>
        <div className="flex items-center gap-1.5" data-testid="gallery-sort">
          <button onClick={() => setSort("trending")} data-testid="gallery-sort-trending"
            className={`mono-chip rounded-full px-3 py-1.5 border flex items-center gap-1.5 transition-colors ${sort === "trending" ? "border-[rgba(20,90,67,0.6)] green-text bg-[rgba(20,90,67,0.12)]" : "border-white/[0.08] text-[var(--bone-faint)]"}`}>
            <Flame className="w-3.5 h-3.5" /> Trending
          </button>
          <button onClick={() => setSort("recent")} data-testid="gallery-sort-recent"
            className={`mono-chip rounded-full px-3 py-1.5 border flex items-center gap-1.5 transition-colors ${sort === "recent" ? "border-[rgba(20,90,67,0.6)] green-text bg-[rgba(20,90,67,0.12)]" : "border-white/[0.08] text-[var(--bone-faint)]"}`}>
            <Clock className="w-3.5 h-3.5" /> Newest
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="gallery-type-filter">
        {TYPES.map((t) => (
          <button key={t || "all"} onClick={() => setType(t)} data-testid={`gallery-filter-${(t || "all").replace(/\s+/g, "-").toLowerCase()}`}
            className={`mono-chip rounded-full px-3 py-1 border transition-colors ${type === t ? "border-[rgba(20,90,67,0.6)] green-text" : "border-white/[0.08] text-[var(--bone-faint)] hover:text-[var(--bone-muted)]"}`}>
            {t || "All"}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44 w-full bg-white/[0.04]" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="litra-card p-10 text-center" data-testid="gallery-empty">
          <Compass className="w-7 h-7 mx-auto text-[var(--bone-faint)]" />
          <p className="mt-3 text-[var(--bone-muted)]">No public decodes yet. Be the first — decode a word and toggle “Share to Gallery”.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="gallery-grid">
          {items.map((it) => <GalleryReportCard key={it.id} item={it} onView={view} />)}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F1115] border-white/[0.1] max-w-2xl max-h-[88vh] overflow-auto scrollbar-thin" data-testid="gallery-detail-dialog">
          <DialogTitle className="sr-only">Decode detail</DialogTitle>
          {loadingDetail && <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          {!loadingDetail && miniData && (
            <div className="space-y-4">
              <DecodeMiniCard data={miniData} decodeType={detail.item.decode_type} />
              <div>
                <div className="mono-chip text-[var(--bone-faint)] mb-2">SHARE THIS WORDBODY</div>
                <ShareCardComposer
                  inputText={miniData.input_text}
                  bindStatement={`${miniData.rule_output?.combined?.litraroot?.technical_name} \u2192 ${miniData.rule_output?.combined?.litraseal?.technical_name}`}
                  signals={(miniData.mini?.top_letter_signals || []).map((s) => `${s.letter} \u00b7 ${s.technical_name}`)}
                  archetype={miniData.mini?.archetype}
                  reportId={detail.item.id}
                  watermark={user?.plan === "free"}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
